import { Link } from "react-router";
import { Loader2, Trophy } from "lucide-react";

import { formatMoney } from "@/lib/format";
import { TDM_CATEGORY_MAP } from "@/lib/tdm-constants";
import type { TdmCategory, TdmContractingRow } from "@/types/tdm";

const MEDAL = ["#f59e0b", "#94a3b8", "#b45309"]; // oro, plata, bronce

export function TopObras({
  rows,
  isLoading,
}: {
  rows: TdmContractingRow[];
  isLoading?: boolean;
}) {
  if (isLoading && rows.length === 0) {
    return (
      <div className="py-16 text-center">
        <Loader2 className="mx-auto size-5 animate-spin text-primary" />
      </div>
    );
  }
  if (rows.length === 0) {
    return (
      <p className="py-16 text-center text-sm text-muted-foreground">
        No hay obras con estos filtros.
      </p>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {rows.slice(0, 3).map((r, i) => (
        <div
          key={r.contracting_id}
          className="flex flex-col rounded-xl border bg-card p-4 shadow-sm"
        >
          <div className="mb-2 flex items-center gap-2">
            <span
              className="flex size-7 items-center justify-center rounded-full text-white"
              style={{ backgroundColor: MEDAL[i] ?? "#94a3b8" }}
            >
              <Trophy className="size-3.5" />
            </span>
            <span className="text-lg font-extrabold tracking-tight">
              {formatMoney(r.amount)}
            </span>
          </div>

          <Link
            to={`/contracting/${r.contracting_id}`}
            className="text-sm font-bold text-primary hover:underline"
          >
            {r.nomenclature}
          </Link>
          <p className="mt-0.5 line-clamp-2 text-xs text-muted-foreground">
            {r.description}
          </p>
          <p className="mt-1.5 text-xs text-muted-foreground">
            <span className="capitalize">{r.department.toLowerCase()}</span>
            <span className="mx-1">·</span>
            <span className="capitalize">{r.sector.toLowerCase()}</span>
          </p>

          {/* Categorías */}
          <div className="mt-2 flex flex-wrap gap-1">
            {r.categorias.map((c) => (
              <span
                key={c}
                className="rounded-full px-2 py-0.5 text-[10px] font-bold text-white"
                style={{
                  backgroundColor:
                    TDM_CATEGORY_MAP[c as TdmCategory]?.color ?? "#94a3b8",
                }}
              >
                {TDM_CATEGORY_MAP[c as TdmCategory]?.label ?? c}
              </span>
            ))}
          </div>

          {/* Productos (formato de bullets de Descubrimiento) */}
          {r.products.length > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {r.products.map((p) => (
                <span
                  key={p}
                  title={p}
                  className="inline-flex max-w-[250px] items-center truncate rounded border border-primary/20 bg-primary/5 px-1.5 py-0.5 text-[10px] font-semibold text-primary"
                >
                  {p}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
